<template>
  <div class="max-h-full max-w-[22rem]">
    <company-logo />

    <div class="text-15 mt-24 text-gray-600 dark:text-gray-200">{{ t('signup.welcomeTitle') }}</div>

    <div class="text-22 mt-4 font-semibold text-gray-800 dark:text-gray-50">{{ t('signup.welcomeMessage') }}</div>

    <div class="my-20 space-y-20">
      <div>
        <bb-checkbox
          :model-value="termsAndConditions"
          class="flex"
          name="termsAndConditions"
          @update:model-value="acceptTerms"
        >
          <i18n-t
            keypath="signup.termsAndPrivacy.legalText"
            tag="div"
            class="text-11 text-gray-700 dark:text-gray-100"
            scope="global"
          >
            <template #termsLink>
              <nuxt-link
                :to="{ name: 'termsOfService', query: { from: route.fullPath } }"
                target="_blank"
                class="text-blue-500 underline"
              >
                {{ t('signup.termsAndPrivacy.termsLink') }}
              </nuxt-link>
            </template>

            <template #privacyLink>
              <nuxt-link :to="privacyPolicyUrl" target="_blank" class="text-blue-500 underline">
                {{ t('signup.termsAndPrivacy.privacyLink') }}
              </nuxt-link>
            </template>
          </i18n-t>
        </bb-checkbox>
        <div v-if="showErrorToAcceptTerms" class="text-11 ml-24 mt-8 text-red-500">
          {{ t('signup.termsAndPrivacy.validation.required') }}
        </div>
      </div>

      <authenticate-provider-button
        provider="github"
        :icon="faGithub"
        auth-type="signup"
        @provider-click="signupWithProvider"
      />
      <authenticate-provider-button
        provider="google"
        :icon="faGoogle"
        auth-type="signup"
        @provider-click="signupWithProvider"
      />

      <div class="text-13 w-full text-center leading-[0.0625rem]">
        <span class="bg-white p-4 text-gray-500 dark:bg-black dark:text-gray-300">
          {{ t('signup.alternativeOption') }}
        </span>
      </div>

      <authenticate-provider-button
        provider="email"
        :icon="faEnvelope"
        auth-type="signup"
        @email-click="signUpWithEmail"
      />
    </div>

    <div class="text-13 text-center">
      <span class="text-gray-600 dark:text-gray-200">{{ t('signup.alreadyRegisteredText') }}</span>

      <nuxt-link :to="{ name: 'login' }" class="ml-4 text-blue-500">{{ t('signup.alreadyRegisteredLink') }}</nuxt-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faGithub, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';

definePageMeta({
  layout: 'authentication',
  name: 'signup',
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const { privacyPolicyUrl } = storeToRefs(useDashboardCustomization());

const termsAndConditions = ref(false);
const showErrorToAcceptTerms = ref(false);

const client = useSupabaseClient();

const confirmPage = { name: 'auth.confirm', query: route.query };

const signupWithProvider = async (provider: OAuthProvider) => {
  if (!termsAndConditions.value) {
    showErrorToAcceptTerms.value = true;
    return;
  }
  const { error } = await client.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: window.location.origin + router.resolve(confirmPage).href,
    },
  });

  if (error) useNotificationBanner(t(useHandleSupabaseError(error)), 'error');
};

const signUpWithEmail = async () => {
  if (!termsAndConditions.value) {
    showErrorToAcceptTerms.value = true;
    return;
  }
  await navigateTo({ name: 'email-signup', query: route.query });
};
const acceptTerms = (value: Array<number | string> | boolean) => {
  if (typeof value !== 'boolean') return;
  termsAndConditions.value = value;
  if (value) showErrorToAcceptTerms.value = false;
};
</script>
